import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Plus,
  Save,
  User,
  Store,
  Calendar,
  DollarSign,
  ShoppingCart,
  CheckCircle,
  AlertCircle,
  Edit,
  X,
  Phone,
  Mail,
  MapPin,
  Stethoscope
} from 'lucide-react';
import { salesOrdersAPI, authAPI, storesAPI, productsAPI, clientsAPI, doctorsAPI, paymentsAPI } from '../services/api';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernOpticalSelect,
  ModernBadge,
  ModernLoadingSpinner,
  ModernModal
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import Select from 'react-select';
import EntitySelectWithAdd from '../components/ui/EntitySelectWithAdd';
import AddProductForm from '../components/forms/AddProductForm';
import AddClientForm from '../components/forms/AddClientForm';
import { useAuth } from '../contexts/AuthContext';
import { formatDateOnlyFr } from '../utils/dateFormat';
import { getApiErrorMessage } from '../utils/apiError';
import { resolveItemPrice, priceInputValue } from '../utils/itemPrice';
import {
  getOpticalEyesFromItems,
  hasOpticalValue,
  isOpticalItemType,
  lensFormFromItem,
  formatOpticalCorrectionSummary,
  parseOpticalNumber,
  resolveOpticalEditState,
  isOpticalModalExpanded,
} from '../utils/opticalValues';
import { SALES_ORDER_STATUS_OPTIONS } from '../constants/salesOrderStatus';
import { COMMANDE_ORIGINE_OPTIONS, COMMANDE_ORIGINE_MAGASIN, COMMANDE_ORIGINE_EN_LIGNE } from '../constants/salesOrderOrigine';

const getEmptyForm = () => ({
  client_id: '',
  doctor_id: '',
  doctor_name: '',
  insurance: '',
  autre_mutuelle: '',
  store_id: '',
  sale_date: '',
  date_reception: '',
  total: '',
  status: 'pas_encore',
  commande_origine: COMMANDE_ORIGINE_MAGASIN,
  order_note: '',
  items: [],
  employee_id: '',
  assignDoctor: false
});

const defaultPayment = {
  amount: '',
  mode: 'espece',
  note: '',
  cheque_number: '',
  payment_date: ''
};

const VentesNouveau = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [form, setForm] = useState(() => getEmptyForm());
  const submitLockRef = useRef(false);
  const [orderError, setOrderError] = useState(null);
  const [products, setProducts] = useState([]);
  const [payment, setPayment] = useState(defaultPayment);
  const [showPaymentSection, setShowPaymentSection] = useState(false);
  const [showDoctorSection, setShowDoctorSection] = useState(false);
  const [employees, setEmployees] = useState([]);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('monture');
  const [modalEditIdx, setModalEditIdx] = useState(null);
  const [modalForm, setModalForm] = useState({ product_id: '', item_type: 'monture', side: 'none', quantity: 1, price: '' });
  const [modalError, setModalError] = useState('');
  const [modalSearchTerm, setModalSearchTerm] = useState('');
  
  // Lens selection modal state
  const [lensSelectionModal, setLensSelectionModal] = useState(false);
  const [lensSelectionTarget, setLensSelectionTarget] = useState('verre');
  const [lensSelectionType, setLensSelectionType] = useState('');
  const [leftLensProduct, setLeftLensProduct] = useState({ product_id: '', price: '' });
  const [rightLensProduct, setRightLensProduct] = useState({ product_id: '', price: '' });
  
  // Optical parameters state
  const [leftLensForm, setLeftLensForm] = useState({ sph: '', cyl: '', axe: '', addition: '' });
  const [rightLensForm, setRightLensForm] = useState({ sph: '', cyl: '', axe: '', addition: '' });
  
  // Lens remark state
  const [lensRemarque, setLensRemarque] = useState('');
  
  // PD and Height measurements state
  const [pdMeasurements, setPdMeasurements] = useState({
    pd_right_verre_droit: '',
    pd_left_verre_droit: '',
    pd_right_verre_gauche: '',
    pd_left_verre_gauche: '',
    hauteur1: '',
    hauteur2: '',
    hauteur3: '',
    hauteur4: ''
  });
  
  // State for showing additional hauteur inputs
  const [showAdditionalHauteurs, setShowAdditionalHauteurs] = useState(false);
  
  // Payment reminder modal state
  const [showPaymentReminderModal, setShowPaymentReminderModal] = useState(false);
  const [pendingOrderData, setPendingOrderData] = useState(null);
  
  // Add product modal state
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  // Add client modal state
  const [showAddClientModal, setShowAddClientModal] = useState(false);

  // Handle product creation success
  const handleProductCreated = async (newProduct) => {
    try {
      // Refresh products list
      await fetchData();
      // Close the modal
      setShowAddProductModal(false);
      // Show success message
      toast.success('Produit ajouté avec succès');
    } catch (error) {
      console.error('Error refreshing products:', error);
    }
  };

  // Handle client creation success
  const handleClientCreated = async (newClient) => {
    try {
      console.log('handleClientCreated called with:', newClient);
      
      // Refresh clients list
      await fetchData();
      // Close the modal
      setShowAddClientModal(false);
      
      // Handle the new API response structure
      // The API returns { clientId: 12, client: { id: 12, name: "..." } }
      const clientId = newClient.clientId || (newClient.client && newClient.client.id) || newClient.id;
      
      console.log('Extracted clientId:', clientId);
      
      if (clientId) {
        // Automatically select the newly created client
        setForm(f => ({ ...f, client_id: clientId.toString() }));
        // Show success message
        toast.success('Client ajouté et sélectionné avec succès');
      } else {
        console.error('No clientId found in response:', newClient);
        toast.error('Erreur: Impossible de récupérer l\'ID du client créé');
      }
    } catch (error) {
      console.error('Error in handleClientCreated:', error);
      toast.error('Erreur lors de la création du client');
    }
  };

  // Helper to get product name
  const getProductName = (id) => {
    // Convert id to number for comparison if it's a string
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    const p = products.find(pr => pr.id === numericId || pr.id === id);
    return p ? p.name : `Produit ${id}`;
  };
  // Helper to get product sale price
  const getProductSalePrice = (id) => {
    // Convert id to number for comparison if it's a string
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    const p = products.find(pr => pr.id === numericId || pr.id === id);
    return p ? p.sale_price : 0;
  };
  const itemPrice = (price, productId) => resolveItemPrice(price, getProductSalePrice(productId));

  // Get filtered products based on type and search term
  const getFilteredProducts = (type, searchTerm = '') => {
    console.log('getFilteredProducts called with type:', type, 'searchTerm:', searchTerm);
    console.log('Total products:', products.length);
    
    // Debug: Log first few products to understand structure
    if (products.length > 0) {
      console.log('Sample product structure:', {
        id: products[0].id,
        name: products[0].name,
        type: products[0].type,
        category_name: products[0].category_name,
        brand: products[0].brand,
        model: products[0].model
      });
    }
    
    let filteredProducts = [];
    
    // First, filter by product type
    switch (type) {
      case 'monture':
        // Only show products that are specifically frames/montures
        filteredProducts = products.filter(p => {
          // Check if it's explicitly a frame type
          const isFrame = p.type === 'frames' || p.type === 'frame' || p.type === 'monture';
          
          // Check if category is specifically for frames
          const isFrameCategory = p.category_name && (
            p.category_name.toLowerCase().includes('monture') ||
            p.category_name.toLowerCase().includes('frame') ||
            p.category_name.toLowerCase().includes('lunettes')
          );
          
          // Check if name indicates it's a frame
          const isFrameName = p.name && (
            p.name.toLowerCase().includes('monture') ||
            p.name.toLowerCase().includes('frame') ||
            p.name.toLowerCase().includes('lunettes')
          );
          
          return isFrame || isFrameCategory || isFrameName;
        });
        console.log('Monture products found:', filteredProducts.length);
        break;
      case 'verre':
        // ONLY show products that have "verre" in their category - SUPER RESTRICTIVE
        filteredProducts = products.filter(p => {
          // ONLY include products where category_name contains "verre"
          if (!p.category_name) {
            return false; // No category = no verre
          }
          
          const categoryLower = p.category_name.toLowerCase();
          const isVerreCategory = categoryLower.includes('verre');
          
          // Debug logging for verre filtering
          if (isVerreCategory) {
            console.log('Verre product found (category match):', {
              id: p.id,
              name: p.name,
              type: p.type,
              category_name: p.category_name,
              isVerreCategory
            });
          }
          
          return isVerreCategory;
        });
        console.log('Verre products found (category only):', filteredProducts.length);
        break;
      case 'lentilles':
        // Only show products that are specifically contact lenses
        filteredProducts = products.filter(p => {
          // Check if it's explicitly a contact lens type
          const isContactLens = p.type === 'contact_lens' || p.type === 'lentilles' || p.type === 'contact';
          
          // Check if category is specifically for contact lenses
          const isContactLensCategory = p.category_name && (
            p.category_name.toLowerCase().includes('lentilles') ||
            p.category_name.toLowerCase().includes('contact') ||
            p.category_name.toLowerCase().includes('lentille')
          );
          
          // Check if name indicates it's a contact lens
          const isContactLensName = p.name && (
            p.name.toLowerCase().includes('lentilles') ||
            p.name.toLowerCase().includes('contact') ||
            p.name.toLowerCase().includes('lentille')
          );
          
          return isContactLens || isContactLensCategory || isContactLensName;
        });
        console.log('Lentilles products found:', filteredProducts.length);
        break;
      case 'accessoire':
        // Only show products that are specifically accessories
        filteredProducts = products.filter(p => {
          // Check if it's explicitly an accessory type
          const isAccessory = p.type === 'accessories' || p.type === 'accessory' || p.type === 'accessoire';
          
          // Check if category is specifically for accessories
          const isAccessoryCategory = p.category_name && (
            p.category_name.toLowerCase().includes('accessoire') ||
            p.category_name.toLowerCase().includes('accessory') ||
            p.category_name.toLowerCase().includes('accessoires')
          );
          
          // Check if name indicates it's an accessory
          const isAccessoryName = p.name && (
            p.name.toLowerCase().includes('accessoire') ||
            p.name.toLowerCase().includes('accessory') ||
            p.name.toLowerCase().includes('accessoires')
          );
          
          return isAccessory || isAccessoryCategory || isAccessoryName;
        });
        console.log('Accessoire products found:', filteredProducts.length);
        break;
      default:
        filteredProducts = products;
        console.log('Default products found:', filteredProducts.length);
    }

    // Then apply search filter - but be more intelligent about it
    if (searchTerm.trim()) {
      const searchLower = searchTerm.trim().toLowerCase();
      console.log(`Searching for "${searchTerm}" within ${filteredProducts.length} filtered products`);
      
      filteredProducts = filteredProducts.filter(p => {
        // Search in name, brand, and model
        const matchesName = p.name && p.name.toLowerCase().includes(searchLower);
        const matchesBrand = p.brand && p.brand.toLowerCase().includes(searchLower);
        const matchesModel = p.model && p.model.toLowerCase().includes(searchLower);
        
        // For verre type, NEVER search in category since we only want verre category products
        let matchesCategory = false;
        if (type === 'verre') {
          // For verre, we don't search in category since all products should already be verre category
          matchesCategory = false;
        } else {
          // For other types, search normally in category
          matchesCategory = p.category_name && p.category_name.toLowerCase().includes(searchLower);
        }
        
        const result = matchesName || matchesBrand || matchesModel || matchesCategory;
        
        if (result && type === 'verre') {
          console.log('Verre product matches search:', {
            id: p.id,
            name: p.name,
            category_name: p.category_name,
            searchTerm: searchTerm,
            matchesName,
            matchesBrand,
            matchesModel,
            matchesCategory
          });
        }
        
        return result;
      });
      
      console.log(`After search filtering: ${filteredProducts.length} products found`);
    }

    // If no products found for verre type, return empty array (no fallback)
    if (type === 'verre' && filteredProducts.length === 0) {
      console.warn('No verre products found. No fallback products will be shown.');
      return [];
    }
    
    return filteredProducts.map(p => ({
      value: p.id,
      label: `${p.name}${p.brand ? ' - ' + p.brand : ''}${p.model ? ' (' + p.model + ')' : ''}`,
      product: p
    }));
  };

  const openModal = (type, idx = null) => {
    console.log('Opening modal with type:', type);
    setModalType(type);
    setModalEditIdx(idx);
    
    // For verre modal, immediately filter and log products
    if (type === 'verre') {
      console.log('=== VERRE MODAL OPENING ===');
      console.log('Total products available:', products.length);
      
      const verreProducts = getFilteredProducts('verre', '');
      console.log('Verre products after filtering:', verreProducts.length);
      
      if (verreProducts.length === 0) {
        console.warn('WARNING: No verre products found!');
      }
    }
    if (idx !== null) {
      const item = form.items[idx];
      setModalForm(item);
      if (isOpticalItemType(type)) {
        const editState = resolveOpticalEditState(form.items, type, item);
        setLensSelectionType(editState.lensSelectionType);
        setLeftLensForm(editState.leftLensForm);
        setRightLensForm(editState.rightLensForm);
        if (editState.leftLensProduct) setLeftLensProduct(editState.leftLensProduct);
        if (editState.rightLensProduct) setRightLensProduct(editState.rightLensProduct);
        setLensRemarque(item.remarque || '');
      }
    } else {
      setModalForm({ product_id: '', item_type: type, side: 'none', quantity: 1, price: '' });
      setLeftLensForm({ sph: '', cyl: '', axe: '', addition: '' });
      setRightLensForm({ sph: '', cyl: '', axe: '', addition: '' });
      setLensRemarque('');
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalForm({ product_id: '', item_type: 'monture', side: 'none', quantity: 1, price: '' });
    setModalEditIdx(null);
    setModalError('');
    setModalSearchTerm('');
    setLeftLensForm({ sph: '', cyl: '', axe: '', addition: '' });
    setRightLensForm({ sph: '', cyl: '', axe: '', addition: '' });
    setLensRemarque('');
    // Don't reset PD measurements - they should persist across lens additions
    setLeftLensProduct({ product_id: '', price: '' });
    setRightLensProduct({ product_id: '', price: '' });
    setLensSelectionType('');
  };

  const handleModalChange = (field, value) => {
    setModalForm(f => ({ ...f, [field]: value }));
  };

  const handleModalSave = () => {
    console.log('handleModalSave called - NOT creating sale, just adding to form');
    
    setModalError('');
    
    if (isOpticalItemType(modalType)) {
      const itemType = modalType;
      const addedMsg = itemType === 'lentilles' ? 'Lentilles ajoutées au panier' : 'Verres ajoutés au panier';
      const addedOneMsg = itemType === 'lentilles' ? 'Lentille ajoutée au panier' : 'Verre ajouté au panier';
      const updatedMsg = itemType === 'lentilles' ? 'Lentilles modifiées avec succès' : 'Verres modifiés avec succès';
      const updatedOneMsg = itemType === 'lentilles' ? 'Lentille modifiée avec succès' : 'Verre modifié avec succès';

      if (lensSelectionType === 'both_different') {
        // Handle different products for left and right eyes
        const leftPrice = itemPrice(leftLensProduct.price, leftLensProduct.product_id);
        const rightPrice = itemPrice(rightLensProduct.price, rightLensProduct.product_id);
        
        const leftLensItem = {
          product_id: leftLensProduct.product_id,
          item_type: itemType,
          side: 'left',
          quantity: modalForm.quantity,
          price: leftPrice,
          ...leftLensForm,
          remarque: lensRemarque
        };
        
        const rightLensItem = {
          product_id: rightLensProduct.product_id,
          item_type: itemType,
          side: 'right',
          quantity: modalForm.quantity,
          price: rightPrice,
          ...rightLensForm,
          remarque: lensRemarque
        };
        
        if (modalEditIdx !== null) {
          const leftItemIndex = form.items.findIndex(item => 
            item.item_type === itemType && item.side === 'left'
          );
          const rightItemIndex = form.items.findIndex(item => 
            item.item_type === itemType && item.side === 'right'
          );
          
          if (leftItemIndex !== -1) {
            setForm(f => ({ ...f, items: f.items.map((it, i) => i === leftItemIndex ? leftLensItem : it) }));
          }
          if (rightItemIndex !== -1) {
            setForm(f => ({ ...f, items: f.items.map((it, i) => i === rightItemIndex ? rightLensItem : it) }));
          }
          toast.success(updatedMsg);
        } else {
          setForm(f => ({ ...f, items: [...f.items, leftLensItem, rightLensItem] }));
          toast.success(addedMsg);
        }
      } else {
        const price = itemPrice(modalForm.price, modalForm.product_id);
        
        if (lensSelectionType === 'both_same' || lensSelectionType === '') {
          const leftLensItem = {
            ...modalForm,
            item_type: itemType,
            side: 'left',
            price,
            ...leftLensForm,
            remarque: lensRemarque
          };
          
          const rightLensItem = {
            ...modalForm,
            item_type: itemType,
            side: 'right',
            price,
            ...rightLensForm,
            remarque: lensRemarque
          };
          
          if (modalEditIdx !== null) {
            const editingLegacy = form.items[modalEditIdx]?.side === 'none';
            if (editingLegacy) {
              setForm(f => ({
                ...f,
                items: [
                  ...f.items.filter((_, i) => i !== modalEditIdx),
                  leftLensItem,
                  rightLensItem,
                ],
              }));
            } else {
              const leftItemIndex = form.items.findIndex(item => 
                item.product_id === modalForm.product_id && 
                item.item_type === itemType && 
                item.side === 'left'
              );
              const rightItemIndex = form.items.findIndex(item => 
                item.product_id === modalForm.product_id && 
                item.item_type === itemType && 
                item.side === 'right'
              );
              
              if (leftItemIndex !== -1) {
                setForm(f => ({ ...f, items: f.items.map((it, i) => i === leftItemIndex ? leftLensItem : it) }));
              }
              if (rightItemIndex !== -1) {
                setForm(f => ({ ...f, items: f.items.map((it, i) => i === rightItemIndex ? rightLensItem : it) }));
              }
            }
            toast.success(updatedMsg);
          } else {
            setForm(f => ({ ...f, items: [...f.items, leftLensItem, rightLensItem] }));
            toast.success(addedMsg);
          }
        } else if (lensSelectionType === 'left_only') {
          const leftLensItem = {
            ...modalForm,
            item_type: itemType,
            side: 'left',
            price,
            ...leftLensForm,
            remarque: lensRemarque
          };
          
          if (modalEditIdx !== null) {
            setForm(f => ({ ...f, items: f.items.map((it, i) => i === modalEditIdx ? leftLensItem : it) }));
            toast.success(updatedOneMsg);
          } else {
            setForm(f => ({ ...f, items: [...f.items, leftLensItem] }));
            toast.success(addedOneMsg);
          }
        } else if (lensSelectionType === 'right_only') {
          const rightLensItem = {
            ...modalForm,
            item_type: itemType,
            side: 'right',
            price,
            ...rightLensForm,
            remarque: lensRemarque
          };
          
          if (modalEditIdx !== null) {
            setForm(f => ({ ...f, items: f.items.map((it, i) => i === modalEditIdx ? rightLensItem : it) }));
            toast.success(updatedOneMsg);
          } else {
            setForm(f => ({ ...f, items: [...f.items, rightLensItem] }));
            toast.success(addedOneMsg);
          }
        }
      }
    } else {
      // Handle non-lens items normally
      const price = itemPrice(modalForm.price, modalForm.product_id);
      const itemToSave = { ...modalForm, price };
    
    if (modalEditIdx !== null) {
      setForm(f => ({ ...f, items: f.items.map((it, i) => i === modalEditIdx ? itemToSave : it) }));
      toast.success('Produit modifié avec succès');
    } else {
      // Allow multiple same products - no duplicate check
      setForm(f => ({ ...f, items: [...f.items, itemToSave] }));
      toast.success('Produit ajouté au panier');
      }
    }
    closeModal();
  };

  const removeItem = (idx) => {
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  // Fetch clients and stores for dropdowns
  const fetchData = async () => {
    setDataLoading(true);
    try {
      const [clientsRes, doctorsRes, storesRes, productsRes, employeesRes] = await Promise.all([
        clientsAPI.getClients(),
        doctorsAPI.getDoctors(),
        storesAPI.getStores(),
        productsAPI.getProducts(),
        authAPI.getEmployees()
      ]);
      // Ensure data is properly structured and has required properties
      const clientsData = Array.isArray(clientsRes.data) ? clientsRes.data : [];
      const doctorsData = Array.isArray(doctorsRes.data) ? doctorsRes.data : [];
      const storesData = Array.isArray(storesRes.data) ? storesRes.data : [];
      const productsData = Array.isArray(productsRes.data) ? productsRes.data : [];
      const employeesData = Array.isArray(employeesRes.data) ? employeesRes.data : [];
      
      setClients(clientsData);
      setDoctors(doctorsData);
      setStores(storesData);
      setProducts(productsData);
      setEmployees(employeesData);
      
      // Debug: Log sample data structures
      if (clientsData.length > 0) {
        console.log('Sample client structure:', clientsData[0]);
      }
      if (doctorsData.length > 0) {
        console.log('Sample doctor structure:', doctorsData[0]);
      }
      if (storesData.length > 0) {
        console.log('Sample store structure:', storesData[0]);
      }
      if (productsData.length > 0) {
        console.log('Sample product structure:', productsData[0]);
      }
      if (employeesData.length > 0) {
        console.log('Sample employee structure:', employeesData[0]);
      }
    } catch (err) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Set store_id automatically for managers and employees
  useEffect(() => {
    if (user && user.role && user.role !== 'admin' && user.store_id) {
      setForm(f => ({ ...f, store_id: user.store_id.toString() }));
    }
  }, [user]);

  const enrichMessageWithProductName = useCallback((message) => {
    if (!message || typeof message !== 'string') return message;
    const match = message.match(/product ID (\d+)/i);
    if (!match) return message;
    const pid = parseInt(match[1], 10);
    const product = products.find((p) => p.id === pid);
    return product?.name ? `${message} — Produit : ${product.name}` : message;
  }, [products]);

  const resetFormToNewOrder = useCallback(() => {
    setOrderError(null);
    const empty = getEmptyForm();
    const nextForm =
      user && user.role && user.role !== 'admin' && user.store_id
        ? { ...empty, store_id: user.store_id.toString() }
        : empty;
    setForm(nextForm);
    setPayment({ ...defaultPayment });
    setShowPaymentSection(false);
    setShowDoctorSection(false);
    setShowPaymentReminderModal(false);
    setPendingOrderData(null);
    setPdMeasurements({
      pd_right_verre_droit: '',
      pd_left_verre_droit: '',
      pd_right_verre_gauche: '',
      pd_left_verre_gauche: '',
      hauteur1: '',
      hauteur2: '',
      hauteur3: '',
      hauteur4: ''
    });
    setShowAdditionalHauteurs(false);
    setLeftLensForm({ sph: '', cyl: '', axe: '', addition: '' });
    setRightLensForm({ sph: '', cyl: '', axe: '', addition: '' });
    setLensRemarque('');
    setLeftLensProduct({ product_id: '', price: '' });
    setRightLensProduct({ product_id: '', price: '' });
    setModalOpen(false);
    setModalEditIdx(null);
    setLensSelectionModal(false);
    setLensSelectionType('');
    setModalForm({ product_id: '', item_type: 'monture', side: 'none', quantity: 1, price: '' });
    setModalError('');
    setModalSearchTerm('');
  }, [user]);

  // Handle form input
  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Handle payment input
  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPayment((p) => ({ ...p, [name]: value }));
  };



  // 1. Add a computed total
  const computedTotal = form.items.reduce(
    (sum, item) => sum + itemPrice(item.price, item.product_id) * Number(item.quantity),
    0
  );

  // Handle submit
  const handleSubmit = async (e) => {
    console.log('handleSubmit called - CREATING SALES ORDER');
    e.preventDefault();
    if (loading || submitLockRef.current) return;
    
    // Check if payment is provided
    if (!payment.amount || payment.amount <= 0) {
      // Show payment reminder modal
      const payload = prepareOrderPayload();
      setPendingOrderData(payload);
      setShowPaymentReminderModal(true);
      return;
    }
    
    // Validate payment details
    if (!validatePayment()) {
      return;
    }
    
    // Proceed with order creation with payment
    await createOrderWithPayment();
  };

  const prepareOrderPayload = () => {
    // Debug form state
    console.log('prepareOrderPayload - Form state:', {
      client_id: form.client_id,
      doctor_id: form.doctor_id,
      doctor_name: form.doctor_name,
      assignDoctor: form.assignDoctor,
      status: form.status
    });
    
    // Fix status: send only the value
    const statusValue = typeof form.status === 'object' && form.status !== null ? form.status.value : form.status;
    // Ensure store_id is a number
    const storeId = typeof form.store_id === 'string' ? parseInt(form.store_id, 10) : form.store_id;
    
    // Extract optical parameters from lens items
    let leftSph = null, leftCyl = null, leftAxis = null, leftAddition = null, leftProductId = null;
    let rightSph = null, rightCyl = null, rightAxis = null, rightAddition = null, rightProductId = null;
    
    // Find lens items and extract optical parameters (verre or lentilles)
    const { left: leftLens, right: rightLens } = getOpticalEyesFromItems(form.items);
    
    if (leftLens) {
      leftSph = parseOpticalNumber(leftLens.sph);
      leftCyl = parseOpticalNumber(leftLens.cyl);
      leftAxis = parseOpticalNumber(leftLens.axe);
      leftAddition = parseOpticalNumber(leftLens.addition);
      leftProductId = leftLens.product_id || null;
    }
    
    if (rightLens) {
      rightSph = parseOpticalNumber(rightLens.sph);
      rightCyl = parseOpticalNumber(rightLens.cyl);
      rightAxis = parseOpticalNumber(rightLens.axe);
      rightAddition = parseOpticalNumber(rightLens.addition);
      rightProductId = rightLens.product_id || null;
    }
    
    const result = { 
      client_id: form.client_id,
      ...(form.doctor_id && form.doctor_id !== '' ? { doctor_id: form.doctor_id } : {}),
      insurance: form.insurance && form.insurance !== '' ? (form.insurance === 'Autre' && form.autre_mutuelle ? form.autre_mutuelle : form.insurance) : 'Aucune',
      ...(form.sale_date && form.sale_date !== '' ? { order_date: form.sale_date } : {}),
      date_reception: form.date_reception && form.date_reception !== '' ? form.date_reception : null,
      status: statusValue, 
      store_id: storeId,
      employee_id: form.employee_id ? parseInt(form.employee_id, 10) : null,
      items: form.items,
      // Optical parameters - only send if lens exists for that eye
      ...(leftLens ? {
        left_sph: leftSph,
        left_cyl: leftCyl,
        left_axis: leftAxis,
        left_addition: leftAddition,
        left_product_id: leftProductId,
      } : {}),
      ...(rightLens ? {
        right_sph: rightSph,
        right_cyl: rightCyl,
        right_axis: rightAxis,
        right_addition: rightAddition,
        right_product_id: rightProductId,
      } : {}),
      // PD and Height measurements
      pd_right_verre_droit: pdMeasurements.pd_right_verre_droit && pdMeasurements.pd_right_verre_droit !== '' ? parseFloat(pdMeasurements.pd_right_verre_droit) : null,
      pd_left_verre_droit: pdMeasurements.pd_left_verre_droit && pdMeasurements.pd_left_verre_droit !== '' ? parseFloat(pdMeasurements.pd_left_verre_droit) : null,
      pd_right_verre_gauche: pdMeasurements.pd_right_verre_gauche && pdMeasurements.pd_right_verre_gauche !== '' ? parseFloat(pdMeasurements.pd_right_verre_gauche) : null,
      pd_left_verre_gauche: pdMeasurements.pd_left_verre_gauche && pdMeasurements.pd_left_verre_gauche !== '' ? parseFloat(pdMeasurements.pd_left_verre_gauche) : null,
      hauteur1: pdMeasurements.hauteur1 && pdMeasurements.hauteur1 !== '' ? parseFloat(pdMeasurements.hauteur1) : null,
      hauteur2: pdMeasurements.hauteur2 && pdMeasurements.hauteur2 !== '' ? parseFloat(pdMeasurements.hauteur2) : null,
      hauteur3: pdMeasurements.hauteur3 && pdMeasurements.hauteur3 !== '' ? parseFloat(pdMeasurements.hauteur3) : null,
      hauteur4: pdMeasurements.hauteur4 && pdMeasurements.hauteur4 !== '' ? parseFloat(pdMeasurements.hauteur4) : null,
      commande_origine:
        form.commande_origine === 'en_ligne' ? 'en_ligne' : 'magasin',
      order_note: form.order_note && form.order_note.trim() !== '' ? form.order_note.trim() : null
    };
    
    console.log('Final payload:', result);
    return result;
  };

  const createOrderWithPayment = async (paymentData = null) => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setLoading(true);
    const payload = paymentData || prepareOrderPayload();
    const paymentToUse = paymentData ? paymentData.payment : payment;
    
    // Validate payment if it exists
    if (paymentToUse && paymentToUse.amount > 0 && !validatePayment()) {
      setLoading(false);
      submitLockRef.current = false;
      return;
    }
    
    console.log('Submitting sales order:', payload, 'User:', user);
    setOrderError(null);
    try {
      const res = await salesOrdersAPI.createSalesOrder(payload);
      const createdOrderId = res.data.salesOrderId;
      
      let paymentRecorded = true;
      // Create payment if amount is provided
      if (paymentToUse.amount && paymentToUse.amount > 0) {
        try {
          await paymentsAPI.createPayment({
            reference_type: 'sales_order',
            reference_id: createdOrderId,
            client_id: form.client_id,
            amount: paymentToUse.amount,
            mode: paymentToUse.mode,
            note: paymentToUse.note,
            cheque_number: paymentToUse.cheque_number,
            payment_date:
              paymentToUse.mode === 'cheque' && paymentToUse.payment_date
                ? paymentToUse.payment_date
                : undefined
          });
          toast.success('Commande créée avec paiement');
        } catch (paymentErr) {
          paymentRecorded = false;
          const payDetail = enrichMessageWithProductName(
            getApiErrorMessage(paymentErr, 'Erreur lors de l\'enregistrement du paiement.')
          );
          const full = `La commande a été créée, mais le paiement n'a pas pu être enregistré : ${payDetail}`;
          setOrderError(full);
          toast.error(full);
          requestAnimationFrame(() => {
            document.getElementById('order-error-banner')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }
      } else {
        toast.success('Commande créée avec succès');
      }
      
      if (paymentRecorded) {
        resetFormToNewOrder();
      }
    } catch (err) {
      console.error('Error creating order:', err);
      const raw = getApiErrorMessage(err, 'Erreur lors de la création de la commande.');
      const detail = enrichMessageWithProductName(raw);
      setOrderError(detail);
      toast.error(detail);
      requestAnimationFrame(() => {
        document.getElementById('order-error-banner')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } finally {
      setLoading(false);
      submitLockRef.current = false;
    }
  };

  const handlePaymentReminderResponse = async (createPayment) => {
    if (createPayment) {
      // User wants to create payment - show payment inputs in modal
      setShowPaymentReminderModal(false);
      setShowPaymentSection(true);
      // Scroll to payment section
      setTimeout(() => {
        const paymentSection = document.getElementById('payment-section');
        if (paymentSection) {
          paymentSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      setShowPaymentReminderModal(false);
      await createOrderWithPayment();
    }
  };
  
  // Validate payment before submission
  const validatePayment = () => {
    if (!payment.amount || payment.amount <= 0) {
      toast.error('Veuillez saisir un montant valide');
      return false;
    }
    
    if (payment.mode === 'cheque' && (!payment.cheque_number || payment.cheque_number.trim() === '')) {
      toast.error('Veuillez saisir le numéro de chèque');
      return false;
    }

    if (payment.mode === 'cheque' && (!payment.payment_date || payment.payment_date.trim() === '')) {
      toast.error('Veuillez saisir la date du chèque');
      return false;
    }
    
    return true;
  };

  const clientOptions = clients && clients.length > 0 ? clients.map(client => ({
    value: client.id?.toString() || '',
    label: client.name || 'Unknown'
  })) : [];

  const doctorOptions = doctors && doctors.length > 0 ? doctors.map(doctor => ({
    value: doctor.id?.toString() || '',
    label: doctor.name || 'Unknown'
  })) : [];

  const insuranceOptions = [
    { value: '', label: 'Aucune' },
    { value: 'CNSS', label: 'CNSS' },
    { value: 'CNOPS', label: 'CNOPS' },
    { value: 'Autre', label: 'Autre' }
  ];

  // Restrict store options for managers (magasin) — all stores when commande en ligne
  const isCommandeEnLigneForm = form.commande_origine === COMMANDE_ORIGINE_EN_LIGNE;
  const canPickAnyStore = Boolean(user && (user.role === 'admin' || isCommandeEnLigneForm));
  const storeOptions =
    stores && stores.length > 0
      ? (!canPickAnyStore && user?.store_id
          ? stores
              .filter((store) => store.id === user.store_id)
              .map((store) => ({ value: store.id?.toString() || '', label: store.name || 'Unknown' }))
          : stores.map((store) => ({
              value: store.id?.toString() || '',
              label: store.name || 'Unknown',
            })))
      : [];

  const statusOptions = SALES_ORDER_STATUS_OPTIONS;

  const paymentModes = [
    { value: 'espece', label: 'Espèce' },
    { value: 'cheque', label: 'Chèque' },
    { value: 'carte_bancaire', label: 'Carte bancaire' },
    { value: 'virement', label: 'Virement' },
  ];

  const opticalModalExpanded = isOpticalModalExpanded(modalType, {
    lensSelectionType,
    modalForm,
    leftLensProduct,
    rightLensProduct,
    isEdit: modalEditIdx !== null,
  });



  if (dataLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Nouvelle Commande</h1>
          <p className="text-neutral-600">Créez une nouvelle commande</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernButton
            variant="secondary"
            onClick={() => resetFormToNewOrder()}
          >
            Réinitialiser
          </ModernButton>
        </div>
      </div>

      {orderError && (
        <div
          id="order-error-banner"
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-4 flex gap-3 items-start shadow-sm"
        >
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-900">Problème lors de l&apos;enregistrement</p>
            <p className="text-sm text-red-800 mt-1 whitespace-pre-wrap break-words">{orderError}</p>
          </div>
          <button
            type="button"
            onClick={() => setOrderError(null)}
            className="flex-shrink-0 p-1 rounded hover:bg-red-100 text-red-700"
            aria-label="Fermer le message"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Form Card */}
      <ModernCard className="p-6 relative">
        {loading && (
          <div
            className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-lg bg-white/85 backdrop-blur-[1px]"
            aria-busy="true"
            aria-live="polite"
            aria-label="Création de la commande en cours"
          >
            <ModernLoadingSpinner size="lg" />
            <p className="mt-4 text-sm font-medium text-neutral-700">Création de la commande...</p>
          </div>
        )}
        <div className="flex items-center mb-6">
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mr-4">
            <ShoppingCart className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Détails de la commande</h3>
            <p className="text-sm text-neutral-600">Remplissez les informations de la commande</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <fieldset disabled={loading} className={loading ? 'opacity-90' : ''}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {/* Client Dropdown with Add Button */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">Client</label>
              <div className="flex space-x-2">
                <div className="flex-1">
                  <ModernSelect
                    name="client_id"
                    value={clients && clients.length > 0 ? clients.map(c => ({ value: c.id?.toString() || '', label: c.name || 'Unknown' })).find(opt => opt.value === form.client_id) || null : null}
                    onChange={option => setForm(f => ({ ...f, client_id: option ? option.value : '' }))}
                    options={clients && clients.length > 0 ? clients.map(c => ({ value: c.id?.toString() || '', label: c.name || 'Unknown' })) : []}
                    placeholder="Sélectionner un client"
                    required
                  />
                </div>
                <ModernButton
                  variant="primary"
                  size="sm"
                  type="button"
                  onClick={() => setShowAddClientModal(true)}
                  className="bg-green-500 hover:bg-green-600 border-green-500 hover:border-green-600"
                >
                  <Plus className="w-4 h-4" />
                </ModernButton>
              </div>
            </div>
            {/* Magasin: libre pour admin, ou pour tous si commande en ligne */}
            {canPickAnyStore ? (
              <ModernSelect
                label="Magasin"
                name="store_id"
                value={storeOptions.find((opt) => opt.value === String(form.store_id)) || null}
                onChange={(option) =>
                  setForm((f) => ({
                    ...f,
                    store_id: option?.value != null ? String(option.value) : '',
                    // Employé filtré par magasin — vider si hors magasin
                    employee_id: '',
                  }))
                }
                options={storeOptions}
                placeholder="Sélectionner un magasin"
                required
              />
            ) : (
              <ModernInput
                label="Magasin"
                name="store_id"
                value={stores.find(s => s.id.toString() === form.store_id)?.name || ''}
                disabled
              />
            )}
            {/* Employee Dropdown: only employees of the selected/fixed store */}
            <ModernSelect
              label="Employé"
              name="employee_id"
              value={employees
                .filter(emp => {
                  if (emp.store_id && form.store_id) {
                    return emp.store_id?.toString() === form.store_id?.toString();
                  } else if (emp.store_name && form.store_id) {
                    const storeObj = stores.find(s => s.id.toString() === form.store_id?.toString());
                    return storeObj && emp.store_name === storeObj.name;
                  }
                  return true;
                })
                .map(emp => ({ value: emp.id.toString(), label: emp.name }))
                .find(opt => opt.value === form.employee_id) || null}
              onChange={option => setForm(f => ({ ...f, employee_id: option ? option.value : '' }))}
              options={employees
                .filter(emp => {
                  if (emp.store_id && form.store_id) {
                    return emp.store_id?.toString() === form.store_id?.toString();
                  } else if (emp.store_name && form.store_id) {
                    const storeObj = stores.find(s => s.id.toString() === form.store_id?.toString());
                    return storeObj && emp.store_name === storeObj.name;
                  }
                  return true;
                })
                .map(emp => ({ value: emp.id.toString(), label: emp.name }))}
              placeholder="Sélectionner l'employé"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ModernSelect
              label="Type de commande"
              name="commande_origine"
              value={
                COMMANDE_ORIGINE_OPTIONS.find((o) => o.value === form.commande_origine) ||
                COMMANDE_ORIGINE_OPTIONS[0]
              }
              onChange={(option) => {
                const nextOrigine = option?.value || COMMANDE_ORIGINE_MAGASIN;
                setForm((f) => {
                  const next = {
                    ...f,
                    commande_origine: nextOrigine,
                  };
                  // Retour magasin : figer le magasin de l'utilisateur (non-admin)
                  if (
                    nextOrigine !== COMMANDE_ORIGINE_EN_LIGNE &&
                    user?.role !== 'admin' &&
                    user?.store_id
                  ) {
                    next.store_id = user.store_id.toString();
                  }
                  return next;
                });
              }}
              options={COMMANDE_ORIGINE_OPTIONS}
            />
            <ModernInput
              label="Date de commande"
              name="sale_date"
              type="date"
              value={form.sale_date}
              onChange={handleChange}
              icon={<Calendar className="w-5 h-5" />}
              required
            />
            <ModernInput
              label="Date de réception"
              name="date_reception"
              type="date"
              value={form.date_reception}
              onChange={handleChange}
              icon={<Calendar className="w-5 h-5" />}
            />
            {/* 2 & 3. Total: read-only, MAD, dynamically calculated */}
            <div>
              <label className="block text-sm font-medium text-neutral-700">Total</label>
              <div className="flex items-center">
                <input
                  className="form-input block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50 font-bold text-lg"
                  type="number"
                  value={computedTotal}
                  readOnly
                  min="0"
                  step="0.01"
                  style={{ background: '#f9fafb' }}
                />
                <span className="ml-2 font-bold text-lg">MAD</span>
              </div>
            </div>
            {/* 1. Statut dropdown: fix onChange to set value directly */}
            <ModernSelect
              label="Statut"
              name="status"
              value={form.status}
              onChange={value => setForm(f => ({ ...f, status: value }))}
              options={statusOptions}
              icon={<CheckCircle className="w-5 h-5" />}
            />
          </div>

          {/* Doctor Assignment Section */}
          <div className="border-t pt-4">
            <div className="flex items-center space-x-3 mb-4">
              <input
                type="checkbox"
                id="assignDoctor"
                checked={showDoctorSection}
                onChange={(e) => {
                  setShowDoctorSection(e.target.checked);
                  if (!e.target.checked) {
                    setForm(f => ({ ...f, doctor_id: '' }));
                  }
                }}
                className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
              />
              <label htmlFor="assignDoctor" className="text-sm font-medium text-gray-700">
                Médecin à affecter
              </label>
            </div>
            
            {showDoctorSection && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <EntitySelectWithAdd
                  entityType="doctor"
                  label="Médecin"
                  options={doctorOptions}
                  value={form.doctor_id}
                  onChange={value => setForm(f => ({ ...f, doctor_id: value }))}
                  fetchOptions={async () => {
                    try {
                      const res = await doctorsAPI.getDoctors();
                      setDoctors(res.data);
                    } catch (error) {
                      console.error('Error refreshing doctors:', error);
                    }
                  }}
                  showAddButton={true}
                />
              </div>
            )}
          </div>

          {/* Insurance Section */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Mutuelle</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <ModernSelect
                label="Type de mutuelle"
                name="insurance"
                value={insuranceOptions.find(opt => opt.value === form.insurance) || null}
                onChange={option => {
                  const insuranceValue = option ? option.value : '';
                  setForm(f => ({
                    ...f,
                    insurance: insuranceValue,
                    // Clear autre_mutuelle if not selecting "Autre"
                    autre_mutuelle: insuranceValue === 'Autre' ? f.autre_mutuelle : ''
                  }));
                }}
                options={insuranceOptions}
                placeholder="Sélectionner..."
              />
              {form.insurance === 'Autre' && (
                <ModernInput
                  label="Autre mutuelle"
                  name="autre_mutuelle"
                  type="text"
                  value={form.autre_mutuelle}
                  onChange={handleChange}
                  placeholder="Saisir le nom de la mutuelle"
                />
              )}
            </div>
          </div>

          {/* Product selection section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-4 mt-4">
            <div className="font-semibold text-primary-700 md:col-span-2">Produits</div>
            <ModernCard className="mb-6 p-4 shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-primary-700">Montures</h3>
                <ModernButton 
                  variant="primary" 
                  size="sm" 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openModal('monture');
                  }}
                >
                  <div className="flex items-center">
                    <Plus className="w-4 h-4" />
                    <span className="ml-2">Ajouter</span>
                  </div>
                </ModernButton>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.items.filter(i => i.item_type === 'monture').map((item, idx) => (
                  <div key={idx} className="flex items-center bg-primary-50 rounded-full px-4 py-2 shadow-sm border border-primary-200">
                    <span className="font-medium mr-2">{getProductName(item.product_id)}</span>
                    <span className="mx-2 text-neutral-500">x{item.quantity}</span>
                    <span className="text-primary-700 font-semibold mr-2">{itemPrice(item.price, item.product_id)} MAD</span>
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openModal('monture', idx);
                      }} 
                      className="ml-1 text-blue-500 hover:text-blue-700"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeItem(idx);
                      }} 
                      className="ml-1 text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {form.items.filter(i => i.item_type === 'monture').length === 0 && (
                  <span className="text-neutral-400">Aucune monture ajoutée</span>
                )}
              </div>
            </ModernCard>
            <ModernCard className="mb-6 p-4 shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-primary-700">Verres</h3>
                <div className="flex items-center space-x-2">
                <ModernButton 
                  variant="primary" 
                  size="sm" 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setLensSelectionTarget('verre');
                    setLensSelectionModal(true);
                  }}
                >
                  <div className="flex items-center">
                    <Plus className="w-4 h-4" />
                    <span className="ml-2">Ajouter</span>
                  </div>
                </ModernButton>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {(() => {
                  const verres = form.items.filter(i => i.item_type === 'verre');
                  
                  // If no verres, show empty state
                  if (verres.length === 0) {
                    return <span className="text-neutral-400">Aucun verre ajouté</span>;
                  }
                  
                  // If products are not loaded yet, show loading
                  if (products.length === 0) {
                    return <span className="text-neutral-400">Chargement des produits...</span>;
                  }
                  
                  // Group verres by product_id
                  const verresByProduct = {};
                  verres.forEach(item => {
                    if (!verresByProduct[item.product_id]) {
                      verresByProduct[item.product_id] = [];
                    }
                    verresByProduct[item.product_id].push(item);
                  });
                  
                                    return Object.entries(verresByProduct).map(([productId, items]) => {
                    const leftLens = items.find(item => item.side === 'left');
                    const rightLens = items.find(item => item.side === 'right');
                    const productName = getProductName(productId);
                    
                    // Show all lenses with proper naming and totals
                    if (leftLens && rightLens) {
                      const idxLeft = form.items.findIndex((it) => it === leftLens);
                      const idxRight = form.items.findIndex((it) => it === rightLens);
                      const leftCorr = formatOpticalCorrectionSummary(leftLens);
                      const rightCorr = formatOpticalCorrectionSummary(rightLens);
                      return (
                        <React.Fragment key={productId}>
                          <div className="flex items-center flex-wrap gap-1 bg-blue-50 rounded-full px-4 py-2 shadow-sm border border-blue-200">
                            <span className="font-medium mr-1">{productName}</span>
                            <span className="text-neutral-500">Gauche</span>
                            {leftCorr && <span className="mx-1 text-xs text-blue-600">{leftCorr}</span>}
                            <span className="mx-1 text-neutral-500">x{leftLens.quantity}</span>
                            <span className="text-blue-700 font-semibold mr-1">
                              {(itemPrice(leftLens.price, leftLens.product_id) * Number(leftLens.quantity)).toFixed(2)} MAD
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openModal('verre', idxLeft);
                              }}
                              className="ml-1 text-blue-500 hover:text-blue-700"
                              title="Modifier le verre gauche"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (idxLeft !== -1) removeItem(idxLeft);
                              }}
                              className="ml-0.5 text-red-500 hover:text-red-700"
                              title="Retirer le verre gauche"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex items-center flex-wrap gap-1 bg-blue-50 rounded-full px-4 py-2 shadow-sm border border-blue-200">
                            <span className="font-medium mr-1">{productName}</span>
                            <span className="text-neutral-500">Droit</span>
                            {rightCorr && <span className="mx-1 text-xs text-blue-600">{rightCorr}</span>}
                            <span className="mx-1 text-neutral-500">x{rightLens.quantity}</span>
                            <span className="text-blue-700 font-semibold mr-1">
                              {(itemPrice(rightLens.price, rightLens.product_id) * Number(rightLens.quantity)).toFixed(2)} MAD
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openModal('verre', idxRight);
                              }}
                              className="ml-1 text-blue-500 hover:text-blue-700"
                              title="Modifier le verre droit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (idxRight !== -1) removeItem(idxRight);
                              }}
                              className="ml-0.5 text-red-500 hover:text-red-700"
                              title="Retirer le verre droit"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </React.Fragment>
                      );
                    } else {
                      // Show individual lenses
                      return items.map((item, idx) => {
                        const itemPriceVal = itemPrice(item.price, item.product_id);
                        const itemTotal = itemPriceVal * Number(item.quantity);
                        const itemIdx = form.items.findIndex(it => it === item);
                        const corr = formatOpticalCorrectionSummary(item);
                        return (
                          <div key={`${productId}-${item.side}`} className="flex items-center bg-blue-50 rounded-full px-4 py-2 shadow-sm border border-blue-200">
                            <span className="font-medium mr-2">{productName}</span>
                            <span className="mx-2 text-neutral-500">{item.side === 'left' ? 'Gauche' : item.side === 'right' ? 'Droit' : ''}</span>
                            {corr && <span className="mx-1 text-xs text-blue-600">{corr}</span>}
                            <span className="mx-2 text-neutral-500">x{item.quantity}</span>
                            <span className="text-blue-700 font-semibold mr-2">{itemTotal.toFixed(2)} MAD</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (itemIdx !== -1) openModal('verre', itemIdx);
                              }}
                              className="ml-1 text-blue-500 hover:text-blue-700"
                              title="Modifier ce verre"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button 
                              type="button"
                              onClick={(e) => { 
                                e.preventDefault(); 
                                e.stopPropagation(); 
                                if (itemIdx !== -1) removeItem(itemIdx);
                              }} 
                              className="ml-1 text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      });
                    }
                  });
                })()}
              </div>
            </ModernCard>
            <ModernCard className="mb-6 p-4 shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-primary-700">Lentilles</h3>
                <ModernButton 
                  variant="primary" 
                  size="sm" 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setLensSelectionTarget('lentilles');
                    setLensSelectionModal(true);
                  }}
                >
                  <div className="flex items-center">
                    <Plus className="w-4 h-4" />
                    <span className="ml-2">Ajouter</span>
                  </div>
                </ModernButton>
              </div>
              <div className="flex flex-wrap gap-2">
                {(() => {
                  const lentilles = form.items.filter(i => i.item_type === 'lentilles');
                  if (lentilles.length === 0) {
                    return <span className="text-neutral-400">Aucune lentille ajoutée</span>;
                  }
                  return lentilles.map((item) => {
                    const fullIdx = form.items.findIndex(it => it === item);
                    const sideLabel = item.side === 'left' ? 'Gauche' : item.side === 'right' ? 'Droit' : '';
                    const corr = formatOpticalCorrectionSummary(item);
                    return (
                      <div key={fullIdx} className="flex items-center bg-purple-50 rounded-full px-4 py-2 shadow-sm border border-purple-200">
                        <span className="font-medium mr-2">{getProductName(item.product_id)}</span>
                        {sideLabel && <span className="mx-1 text-neutral-500">{sideLabel}</span>}
                        {corr && <span className="mx-1 text-xs text-purple-600">{corr}</span>}
                        <span className="mx-2 text-neutral-500">x{item.quantity}</span>
                        <span className="text-purple-700 font-semibold mr-2">{itemPrice(item.price, item.product_id)} MAD</span>
                        <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openModal('lentilles', fullIdx); }} className="ml-1 text-blue-500 hover:text-blue-700"><Edit className="w-4 h-4" /></button>
                        <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeItem(fullIdx); }} className="ml-1 text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
                      </div>
                    );
                  });
                })()}
              </div>
            </ModernCard>
            <ModernCard className="mb-6 p-4 shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-primary-700">Accessoires</h3>
                <ModernButton 
                  variant="primary" 
                  size="sm" 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openModal('accessoire');
                  }}
                >
                  <div className="flex items-center">
                    <Plus className="w-4 h-4" />
                    <span className="ml-2">Ajouter</span>
                  </div>
                </ModernButton>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.items.filter(i => i.item_type === 'accessoire').map((item) => {
                  const fullIdx = form.items.findIndex(it => it === item);
                  return (
                    <div key={fullIdx} className="flex items-center bg-green-50 rounded-full px-4 py-2 shadow-sm border border-green-200">
                      <span className="font-medium mr-2">{getProductName(item.product_id)}</span>
                      <span className="mx-2 text-neutral-500">x{item.quantity}</span>
                      <span className="text-green-700 font-semibold mr-2">{itemPrice(item.price, item.product_id)} MAD</span>
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openModal('accessoire', fullIdx);
                        }} 
                        className="ml-1 text-blue-500 hover:text-blue-700"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeItem(fullIdx);
                        }} 
                        className="ml-1 text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
                {form.items.filter(i => i.item_type === 'accessoire').length === 0 && (
                  <span className="text-neutral-400">Aucun accessoire ajouté</span>
                )}
              </div>
            </ModernCard>
          </div>

          {/* Payment Section */}
          <div className="border-t pt-6 mt-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="addPayment"
                  checked={showPaymentSection}
                  onChange={(e) => setShowPaymentSection(e.target.checked)}
                  className="w-5 h-5 text-primary-600 bg-white border-yellow-400 rounded focus:ring-primary-500 focus:ring-2"
                />
                <label htmlFor="addPayment" className="text-base font-semibold text-yellow-800">
                  💳 Ajouter un paiement
                </label>
                <span className="text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full">
                  Important
                </span>
              </div>
              <p className="text-sm text-yellow-700 mt-2 ml-8">
                N'oubliez pas d'ajouter le paiement pour cette commande
              </p>
            </div>
            
            {showPaymentSection && (
              <div id="payment-section" className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 p-4 rounded-lg">
                <ModernInput
                  label="Montant (MAD)"
                  name="amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={payment.amount}
                  onChange={handlePaymentChange}
                  placeholder="0.00"
                  icon={<DollarSign className="w-5 h-5" />}
                />
                <ModernSelect
                  label="Mode de paiement"
                  name="mode"
                  value={paymentModes.find(opt => opt.value === payment.mode) || paymentModes[0]}
                  onChange={option =>
                    setPayment((p) => {
                      const selectedMode = option ? option.value : 'espece';
                      if (selectedMode !== 'cheque') {
                        return {
                          ...p,
                          mode: selectedMode,
                          cheque_number: '',
                          payment_date: ''
                        };
                      }
                      return { ...p, mode: selectedMode };
                    })
                  }
                  options={paymentModes}
                  icon={<DollarSign className="w-5 h-5" />}
                />
                {payment.mode === 'cheque' && (
                  <>
                    <ModernInput
                      label="Numéro de chèque"
                      name="cheque_number"
                      value={payment.cheque_number}
                      onChange={handlePaymentChange}
                      placeholder="Entrez le numéro de chèque..."
                      icon={<CheckCircle className="w-5 h-5" />}
                      required
                    />
                    <ModernInput
                      label="Date du chèque"
                      name="payment_date"
                      type="date"
                      value={payment.payment_date || ''}
                      onChange={handlePaymentChange}
                      icon={<Calendar className="w-5 h-5" />}
                      required
                    />
                  </>
                )}
                <ModernInput
                  label="Note"
                  name="note"
                  value={payment.note}
                  onChange={handlePaymentChange}
                  placeholder="Ex: Avance, acompte..."
                  icon={<CheckCircle className="w-5 h-5" />}
                />
              </div>
            )}
          </div>

          <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Note commande</h3>
            <textarea
              name="order_note"
              value={form.order_note || ''}
              onChange={(e) => setForm((f) => ({ ...f, order_note: e.target.value }))}
              placeholder="Ajouter une note générale pour cette commande..."
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
            <p className="mt-1 text-xs text-neutral-500">
              Cette note concerne toute la commande. Les remarques par produit (verres, etc.) restent inchangées.
            </p>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-neutral-200">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => resetFormToNewOrder()}
              disabled={loading}
            >
              Réinitialiser
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={loading ? null : <Save className="w-4 h-4" />}
              disabled={loading}
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <ModernLoadingSpinner size="sm" />
                  Création...
                </span>
              ) : (
                'Créer la commande'
              )}
            </ModernButton>
          </div>
          </fieldset>
        </form>
      </ModernCard>

      {/* Preview Card */}
                {form.client_id && form.store_id && (
        <ModernCard className="p-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-success-100 rounded-lg flex items-center justify-center mr-3">
              <CheckCircle className="w-5 h-5 text-success-600" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900">Aperçu de la commande</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {form.sale_date && (
              <div className="p-4 bg-neutral-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
                  <span className="text-sm font-medium text-neutral-600">Date de commande</span>
                </div>
                <p className="font-semibold text-neutral-900">{formatDateOnlyFr(form.sale_date)}</p>
              </div>
            )}
            <div className="p-4 bg-neutral-50 rounded-lg">
              <div className="flex items-center mb-2">
                <User className="w-4 h-4 text-neutral-400 mr-2" />
                <span className="text-sm font-medium text-neutral-600">Client</span>
              </div>
              <p className="font-semibold text-neutral-900">
                {clients && clients.length > 0 && form.client_id ? clients.find(c => c.id && c.id.toString() === form.client_id)?.name || '-' : '-'}
              </p>
            </div>

            <div className="p-4 bg-neutral-50 rounded-lg">
              <div className="flex items-center mb-2">
                <Store className="w-4 h-4 text-neutral-400 mr-2" />
                <span className="text-sm font-medium text-neutral-600">Magasin</span>
              </div>
              <p className="font-semibold text-neutral-900">
                {stores && stores.length > 0 && form.store_id ? stores.find(s => s.id && s.id.toString() === form.store_id)?.name || '-' : '-'}
              </p>
            </div>

            <div className="p-4 bg-neutral-50 rounded-lg">
              <div className="flex items-center mb-2">
                <User className="w-4 h-4 text-neutral-400 mr-2" />
                <span className="text-sm font-medium text-neutral-600">Médecin</span>
              </div>
              <p className="font-semibold text-neutral-900">
                {form.doctor_id && doctors && doctors.length > 0 ? doctors.find(d => d.id && d.id.toString() === form.doctor_id)?.name || '-' : 'Non assigné'}
              </p>
            </div>

            <div className="p-4 bg-neutral-50 rounded-lg">
              <div className="flex items-center mb-2">
                <CheckCircle className="w-4 h-4 text-neutral-400 mr-2" />
                <span className="text-sm font-medium text-neutral-600">Mutuelle</span>
              </div>
              <p className="font-semibold text-neutral-900">
                {form.insurance === 'Autre' && form.autre_mutuelle ? form.autre_mutuelle : (form.insurance || 'Aucune')}
              </p>
            </div>

            {form.date_reception && (
              <div className="p-4 bg-neutral-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
                  <span className="text-sm font-medium text-neutral-600">Date de réception</span>
                </div>
                <p className="font-semibold text-neutral-900">
                  {formatDateOnlyFr(form.date_reception)}
                </p>
              </div>
            )}

            <div className="p-4 bg-neutral-50 rounded-lg">
              <div className="flex items-center mb-2">
                <DollarSign className="w-4 h-4 text-neutral-400 mr-2" />
                <span className="text-sm font-medium text-neutral-600">Total</span>
              </div>
              <p className="font-semibold text-success-600">
                {computedTotal ? new Intl.NumberFormat('fr-FR', {
                  style: 'currency',
                  currency: 'MAD',
                }).format(computedTotal) : '0,00 MAD'}
              </p>
            </div>

            {showPaymentSection && payment.amount && (
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <DollarSign className="w-4 h-4 text-green-400 mr-2" />
                  <span className="text-sm font-medium text-green-600">Paiement</span>
                </div>
                <p className="font-semibold text-green-700">
                  {new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'MAD',
                  }).format(payment.amount)}
                </p>
                <p className="text-xs text-green-600">
                  {paymentModes.find(m => m.value === payment.mode)?.label} 
                  {payment.mode === 'cheque' && payment.cheque_number && ` - Chèque #${payment.cheque_number}`}
                  {payment.mode === 'cheque' && payment.payment_date && ` - Date ${new Date(payment.payment_date).toLocaleDateString('fr-FR')}`}
                  {payment.mode !== 'cheque' && payment.note && ` - ${payment.note}`}
                </p>
              </div>
            )}
          </div>
        </ModernCard>
      )}

      {/* Lens Selection Modal */}
      <ModernModal isOpen={lensSelectionModal} onClose={() => setLensSelectionModal(false)} title={lensSelectionTarget === 'lentilles' ? 'Sélection du type de lentille' : 'Sélection du type de verre'} size="md">
        <div className="space-y-4">
          <div className="text-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {lensSelectionTarget === 'lentilles' ? 'Choisissez le type de lentille' : 'Choisissez le type de verre'}
            </h3>
            <p className="text-sm text-gray-600">
              {lensSelectionTarget === 'lentilles'
                ? 'Sélectionnez comment vous souhaitez ajouter les lentilles'
                : 'Sélectionnez comment vous souhaitez ajouter les verres'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 gap-4">
            <button
              onClick={() => {
                setLensSelectionType('both_same');
                setLensSelectionModal(false);
                // Reset product selections for same product
                setLeftLensProduct({ product_id: '', price: '' });
                setRightLensProduct({ product_id: '', price: '' });
                openModal(lensSelectionTarget);
              }}
              className="p-4 border-2 border-blue-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-blue-700">
                    {lensSelectionTarget === 'lentilles' ? 'Deux yeux - Même lentille' : 'Deux yeux - Même verre'}
                  </h4>
                  <p className="text-sm text-gray-600">Un seul produit pour les deux yeux</p>
                </div>
                <div className="text-blue-500">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => {
                setLensSelectionType('both_different');
                setLensSelectionModal(false);
                // Reset product selections for different products
                setLeftLensProduct({ product_id: '', price: '' });
                setRightLensProduct({ product_id: '', price: '' });
                openModal(lensSelectionTarget);
              }}
              className="p-4 border-2 border-green-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-green-700">
                    {lensSelectionTarget === 'lentilles' ? 'Deux yeux - Lentilles différentes' : 'Deux yeux - Verres différents'}
                  </h4>
                  <p className="text-sm text-gray-600">Deux produits différents (gauche et droit)</p>
                </div>
                <div className="text-green-500">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => {
                setLensSelectionType('left_only');
                setLensSelectionModal(false);
                openModal(lensSelectionTarget);
              }}
              className="p-4 border-2 border-orange-200 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-colors text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-orange-700">Œil gauche seulement</h4>
                  <p className="text-sm text-gray-600">
                    {lensSelectionTarget === 'lentilles'
                      ? "Uniquement la lentille pour l'œil gauche"
                      : "Uniquement le verre pour l'œil gauche"}
                  </p>
                </div>
                <div className="text-orange-500">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => {
                setLensSelectionType('right_only');
                setLensSelectionModal(false);
                openModal(lensSelectionTarget);
              }}
              className="p-4 border-2 border-purple-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-purple-700">Œil droit seulement</h4>
                  <p className="text-sm text-gray-600">
                    {lensSelectionTarget === 'lentilles'
                      ? "Uniquement la lentille pour l'œil droit"
                      : "Uniquement le verre pour l'œil droit"}
                  </p>
                </div>
                <div className="text-purple-500">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              </div>
            </button>
          </div>
        </div>
      </ModernModal>

      {/* Modal for add/edit */}
      <ModernModal
        isOpen={modalOpen}
        onClose={closeModal}
        title={
          modalEditIdx !== null
            ? (modalType === 'lentilles' ? 'Modifier les lentilles' : modalType === 'verre' ? 'Modifier le verre' : `Modifier le ${modalType}`)
            : (modalType === 'lentilles' ? 'Ajouter des lentilles' : modalType === 'verre' ? 'Ajouter un verre' : `Ajouter un ${modalType}`)
        }
        size={opticalModalExpanded ? 'wide' : 'lg'}
        className={opticalModalExpanded ? 'optical-product-modal' : ''}
      >
        <div className={opticalModalExpanded ? 'optical-modal-layout' : 'space-y-4'}>
          <div className="space-y-3 min-w-0">
          {/* Product Type Description */}
          {!isOpticalItemType(modalType) && (
          <div className="text-center mb-2">
            <p className="text-sm text-neutral-600">
              {modalType === 'monture' && 'Sélectionnez des montures de lunettes'}
              {modalType === 'accessoire' && 'Sélectionnez des accessoires'}
            </p>
          </div>
          )}
          
          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder={`Rechercher ${modalType === 'monture' ? 'une monture' : modalType === 'verre' ? 'un verre' : modalType === 'lentilles' ? 'une lentille' : 'un accessoire'}...`}
              value={modalSearchTerm}
              onChange={(e) => setModalSearchTerm(e.target.value)}
              className="w-full px-4 py-3 pr-20 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="absolute right-12 top-3 text-neutral-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <button
              type="button"
              onClick={() => setShowAddProductModal(true)}
              className="absolute right-2 top-2 h-8 w-8 flex items-center justify-center bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
              title="Ajouter un nouveau produit"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          {/* Warning if no products of specific type found */}
          {modalSearchTerm.trim() && getFilteredProducts(modalType, modalSearchTerm).length === 0 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-700">
                ⚠️ Aucun produit de type "{modalType}" trouvé.
              </p>
            </div>
          )}
          
          {/* Product List */}
          {modalSearchTerm.trim() ? (
            <div className={`${isOpticalItemType(modalType) ? 'max-h-44' : 'max-h-64'} overflow-y-auto border border-neutral-200 rounded-lg`}>
              {getFilteredProducts(modalType, modalSearchTerm).length === 0 ? (
                <div className="p-4 text-center text-neutral-500">
                  Aucun produit trouvé
                </div>
              ) : (
                <div className="divide-y divide-neutral-200">
                  {getFilteredProducts(modalType, modalSearchTerm).map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        if (isOpticalItemType(modalType) && lensSelectionType === 'both_different') {
                          // For different products, show selection for left or right
                          if (!leftLensProduct.product_id) {
                            setLeftLensProduct({ product_id: option.value, price: option.product.sale_price });
                          } else if (!rightLensProduct.product_id) {
                            setRightLensProduct({ product_id: option.value, price: option.product.sale_price });
                          }
                        } else {
                          // For same product or single eye, use normal selection
                          handleModalChange('product_id', option.value);
                        }
                      }}
                      className={`p-3 cursor-pointer hover:bg-neutral-50 transition-colors ${
                        (modalForm.product_id === option.value) || 
                        (isOpticalItemType(modalType) && lensSelectionType === 'both_different' && 
                         (leftLensProduct.product_id === option.value || rightLensProduct.product_id === option.value))
                          ? 'bg-primary-50 border-l-4 border-primary-500' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-neutral-900">{option.product.name}</div>
                          <div className="text-sm text-neutral-600">
                            {option.product.brand && `${option.product.brand}`}
                            {option.product.model && ` - ${option.product.model}`}
                            {option.product.category_name && ` (${option.product.category_name})`}
                          </div>
                          <div className="text-sm text-success-600 font-semibold">
                            {option.product.sale_price} MAD
                          </div>
                          {isOpticalItemType(modalType) && lensSelectionType === 'both_different' && (
                            <div className="text-xs text-blue-600 mt-1">
                              {leftLensProduct.product_id === option.value ? `← ${modalType === 'lentilles' ? 'Lentille Gauche' : 'Verre Gauche'}` : 
                               rightLensProduct.product_id === option.value ? `→ ${modalType === 'lentilles' ? 'Lentille Droit' : 'Verre Droit'}` : ''}
                        </div>
                          )}
                        </div>
                        {(modalForm.product_id === option.value) || 
                         (isOpticalItemType(modalType) && lensSelectionType === 'both_different' && 
                          (leftLensProduct.product_id === option.value || rightLensProduct.product_id === option.value)) ? (
                          <div className="text-primary-600">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 text-center text-neutral-500 border border-neutral-200 rounded-lg bg-neutral-50">
              <svg className="w-12 h-12 mx-auto mb-3 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-lg font-medium mb-2">Recherchez un produit</p>
              <p className="text-sm">Commencez à taper pour rechercher parmi vos produits</p>
            </div>
          )}

          </div>

          <div className="space-y-3 min-w-0">

          {/* Selected Product Info */}
          {isOpticalItemType(modalType) && lensSelectionType === 'both_different' ? (
            <div className="space-y-3">
              {/* Left Lens Product */}
              {leftLensProduct.product_id && (
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <div className="text-blue-700 font-semibold">
                    {modalType === 'lentilles' ? 'Lentille Gauche' : 'Verre Gauche'}: {getProductName(leftLensProduct.product_id)}
                    {formatOpticalCorrectionSummary(leftLensForm) && (
                      <span className="ml-2 font-normal text-blue-600">{formatOpticalCorrectionSummary(leftLensForm)}</span>
                    )}
                  </div>
                  <div className="text-sm text-blue-600">
                    Prix: {getProductSalePrice(leftLensProduct.product_id)} MAD
                  </div>
                </div>
              )}
              
              {/* Right Lens Product */}
              {rightLensProduct.product_id && (
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <div className="text-green-700 font-semibold">
                    {modalType === 'lentilles' ? 'Lentille Droit' : 'Verre Droit'}: {getProductName(rightLensProduct.product_id)}
                    {formatOpticalCorrectionSummary(rightLensForm) && (
                      <span className="ml-2 font-normal text-green-600">{formatOpticalCorrectionSummary(rightLensForm)}</span>
                    )}
                  </div>
                  <div className="text-sm text-green-600">
                    Prix: {getProductSalePrice(rightLensProduct.product_id)} MAD
                  </div>
                </div>
              )}
              
              {/* Selection Status */}
              {(!leftLensProduct.product_id || !rightLensProduct.product_id) && (
                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                  <div className="text-yellow-700 text-sm">
                    {!leftLensProduct.product_id && !rightLensProduct.product_id ? 
                      (modalType === 'lentilles' ? "Sélectionnez d'abord la lentille gauche, puis la lentille droite" : "Sélectionnez d'abord le verre gauche, puis le verre droit") :
                      !leftLensProduct.product_id ? 
                      (modalType === 'lentilles' ? 'Sélectionnez la lentille gauche' : 'Sélectionnez le verre gauche') :
                      (modalType === 'lentilles' ? 'Sélectionnez la lentille droite' : 'Sélectionnez le verre droit')
                    }
                  </div>
                </div>
              )}
            </div>
          ) : modalForm.product_id && (
            <div className="bg-primary-50 p-3 rounded-lg">
              <div className="text-primary-700 font-semibold">
                Produit sélectionné: {getProductName(modalForm.product_id)}
              </div>
              <div className="text-sm text-primary-600">
                Prix: {getProductSalePrice(modalForm.product_id)} MAD
              </div>
              {isOpticalItemType(modalType) && (
                <div className="mt-2 space-y-1">
                  <div className="text-xs text-primary-600">
                    <span className="font-medium">{modalType === 'lentilles' ? 'Lentille Gauche:' : 'Verre Gauche:'}</span>{' '}
                    {formatOpticalCorrectionSummary(leftLensForm) || '—'}
                  </div>
                  <div className="text-xs text-primary-600">
                    <span className="font-medium">{modalType === 'lentilles' ? 'Lentille Droit:' : 'Verre Droit:'}</span>{' '}
                    {formatOpticalCorrectionSummary(rightLensForm) || '—'}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Remarque field for lenses - Show for all verre types */}
          {isOpticalItemType(modalType) && (modalForm.product_id || (lensSelectionType === 'both_different' && leftLensProduct.product_id && rightLensProduct.product_id)) && (
            <div className="optical-modal-compact-panel border-purple-200 bg-purple-50">
              <h4 className="font-semibold text-purple-700 mb-2 text-sm">Remarque</h4>
              <textarea
                value={lensRemarque}
                onChange={(e) => setLensRemarque(e.target.value)}
                placeholder={modalType === 'lentilles' ? 'Remarque lentille...' : 'Remarque verre...'}
                rows="2"
                className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              />
            </div>
          )}

          {/* Optical parameters for glasses */}
          {isOpticalItemType(modalType) && (modalForm.product_id || (lensSelectionType === 'both_different' && (leftLensProduct.product_id || rightLensProduct.product_id))) && (
            <div className="optical-eyes-grid">
              {/* Right Eye - Show for both_same, both_different, and right_only */}
              {(lensSelectionType === 'both_same' || lensSelectionType === 'both_different' || lensSelectionType === 'right_only' || lensSelectionType === '') && (
                <div className="optical-modal-compact-panel border-green-200 bg-green-50">
                  <h4 className="font-semibold text-green-700 mb-2 text-sm">Oeil Droit</h4>
                  <div className="optical-eye-fields">
                    <ModernOpticalSelect 
                      label="Sph" 
                      value={rightLensForm.sph || ''} 
                      onChange={e => setRightLensForm(prev => ({ ...prev, sph: e.target.value }))} 
                      placeholder="Sélectionner Sph" 
                    />
                    <ModernOpticalSelect 
                      label="Cyl" 
                      value={rightLensForm.cyl || ''} 
                      onChange={e => setRightLensForm(prev => ({ ...prev, cyl: e.target.value }))} 
                      placeholder="Sélectionner Cyl" 
                    />
                    <ModernInput 
                      label="Axe" 
                      type="number" 
                      step="0.01"
                      value={rightLensForm.axe || ''} 
                      onChange={e => setRightLensForm(prev => ({ ...prev, axe: e.target.value }))} 
                      placeholder="Axe"
                      className="no-spinner"
                    />
                    <ModernInput 
                      label="Addition" 
                      type="number" 
                      step="0.01" 
                      value={rightLensForm.addition || ''} 
                      onChange={e => setRightLensForm(prev => ({ ...prev, addition: e.target.value }))} 
                      placeholder="Addition"
                      className="no-spinner"
                    />
                  </div>
                </div>
              )}

              {/* Left Eye - Show for both_same, both_different, and left_only */}
              {(lensSelectionType === 'both_same' || lensSelectionType === 'both_different' || lensSelectionType === 'left_only' || lensSelectionType === '') && (
                <div className="optical-modal-compact-panel border-blue-200 bg-blue-50">
                  <h4 className="font-semibold text-blue-700 mb-2 text-sm">Oeil Gauche</h4>
                  <div className="optical-eye-fields">
                    <ModernOpticalSelect 
                      label="Sph" 
                      value={leftLensForm.sph || ''} 
                      onChange={e => setLeftLensForm(prev => ({ ...prev, sph: e.target.value }))} 
                      placeholder="Sélectionner Sph" 
                    />
                    <ModernOpticalSelect 
                      label="Cyl" 
                      value={leftLensForm.cyl || ''} 
                      onChange={e => setLeftLensForm(prev => ({ ...prev, cyl: e.target.value }))} 
                      placeholder="Sélectionner Cyl" 
                    />
                    <ModernInput 
                      label="Axe" 
                      type="number" 
                      step="0.01"
                      value={leftLensForm.axe || ''} 
                      onChange={e => setLeftLensForm(prev => ({ ...prev, axe: e.target.value }))} 
                      placeholder="Axe"
                      className="no-spinner"
                    />
                    <ModernInput 
                      label="Addition" 
                      type="number" 
                      step="0.01" 
                      value={leftLensForm.addition || ''} 
                      onChange={e => setLeftLensForm(prev => ({ ...prev, addition: e.target.value }))} 
                      placeholder="Addition"
                      className="no-spinner"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PD and Height Measurements — verres only */}
          {modalType === 'verre' && (modalForm.product_id || (lensSelectionType === 'both_different' && (leftLensProduct.product_id || rightLensProduct.product_id))) && (
            <div className="space-y-3">
              <div className="optical-modal-compact-panel border-gray-200 bg-gray-50">
                <h4 className="font-semibold text-gray-700 mb-2 text-sm">Mesures Pupillaires</h4>
                
                {/* Show different PD fields based on lens selection type */}
                {(lensSelectionType === 'both_same' || lensSelectionType === '') && (
                  <div className="optical-pd-pair">
                    {/* Vision de loin section */}
                    <div className="optical-modal-compact-panel border-blue-200 bg-blue-50">
                      <h5 className="font-semibold text-blue-700 mb-2 text-sm text-center">Vision de loin</h5>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-sm font-medium text-blue-600 mb-1">ÉCART DROIT (verre droit)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={pdMeasurements.pd_right_verre_droit || ''}
                            onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_right_verre_droit: e.target.value }))}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 no-spinner"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-blue-600 mb-1">ÉCART GAUCHE (verre droit)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={pdMeasurements.pd_left_verre_droit || ''}
                            onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_left_verre_droit: e.target.value }))}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 no-spinner"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Vision de près section */}
                    <div className="optical-modal-compact-panel border-orange-200 bg-orange-50">
                      <h5 className="font-semibold text-orange-700 mb-2 text-sm text-center">Vision de près</h5>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-sm font-medium text-orange-600 mb-1">ÉCART DROIT (verre gauche)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={pdMeasurements.pd_right_verre_gauche || ''}
                            onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_right_verre_gauche: e.target.value }))}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-orange-600 mb-1">ÉCART GAUCHE (verre gauche)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={pdMeasurements.pd_left_verre_gauche || ''}
                            onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_left_verre_gauche: e.target.value }))}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* For different lenses, show separate sections */}
                {lensSelectionType === 'both_different' && (
                  <div className="optical-pd-pair">
                    <div className="optical-modal-compact-panel border-blue-200 bg-blue-50">
                      <h5 className="font-semibold text-blue-700 mb-2 text-sm text-center">Vision de loin</h5>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-sm font-medium text-blue-600 mb-1">ÉCART DROIT (verre droit)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={pdMeasurements.pd_right_verre_droit || ''}
                            onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_right_verre_droit: e.target.value }))}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 no-spinner"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-blue-600 mb-1">ÉCART GAUCHE (verre droit)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={pdMeasurements.pd_left_verre_droit || ''}
                            onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_left_verre_droit: e.target.value }))}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 no-spinner"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Vision de près section */}
                    <div className="optical-modal-compact-panel border-orange-200 bg-orange-50">
                      <h5 className="font-semibold text-orange-700 mb-2 text-sm text-center">Vision de près</h5>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-sm font-medium text-orange-600 mb-1">ÉCART DROIT (verre gauche)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={pdMeasurements.pd_right_verre_gauche || ''}
                            onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_right_verre_gauche: e.target.value }))}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-orange-600 mb-1">ÉCART GAUCHE (verre gauche)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={pdMeasurements.pd_left_verre_gauche || ''}
                            onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_left_verre_gauche: e.target.value }))}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* For single lens (left only) - Vision de près only */}
                {lensSelectionType === 'left_only' && (
                  <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                    <h5 className="font-semibold text-orange-700 mb-3 text-center">Vision de près - Verre Gauche Seulement</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-orange-600 mb-1">ÉCART DROIT (verre gauche)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={pdMeasurements.pd_right_verre_gauche || ''}
                          onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_right_verre_gauche: e.target.value }))}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-orange-600 mb-1">ÉCART GAUCHE (verre gauche)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={pdMeasurements.pd_left_verre_gauche || ''}
                          onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_left_verre_gauche: e.target.value }))}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* For single lens (right only) - Vision de loin only */}
                {lensSelectionType === 'right_only' && (
                  <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                    <h5 className="font-semibold text-blue-700 mb-3 text-center">Vision de loin - Verre Droit Seulement</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-600 mb-1">ÉCART DROIT (verre droit)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={pdMeasurements.pd_right_verre_droit || ''}
                          onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_right_verre_droit: e.target.value }))}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-600 mb-1">ÉCART GAUCHE (verre droit)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={pdMeasurements.pd_left_verre_droit || ''}
                          onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_left_verre_droit: e.target.value }))}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="optical-modal-compact-panel border-orange-200 bg-orange-50">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-orange-700 text-sm">Mesures de Hauteur</h4>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="showAdditionalHauteurs"
                      checked={showAdditionalHauteurs}
                      onChange={(e) => setShowAdditionalHauteurs(e.target.checked)}
                      className="rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                    />
                    <label htmlFor="showAdditionalHauteurs" className="text-xs font-medium text-orange-600">
                      H. 3 & 4
                    </label>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-orange-600 mb-1">HAUTEUR 1</label>
                    <input
                      type="number"
                      step="0.01"
                      value={pdMeasurements.hauteur1 || ''}
                      onChange={(e) => setPdMeasurements(prev => ({ ...prev, hauteur1: e.target.value }))}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-orange-600 mb-1">HAUTEUR 2</label>
                    <input
                      type="number"
                      step="0.01"
                      value={pdMeasurements.hauteur2 || ''}
                      onChange={(e) => setPdMeasurements(prev => ({ ...prev, hauteur2: e.target.value }))}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                    />
                  </div>
                  {showAdditionalHauteurs && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-orange-600 mb-1">HAUTEUR 3</label>
                        <input
                          type="number"
                          step="0.01"
                          value={pdMeasurements.hauteur3 || ''}
                          onChange={(e) => setPdMeasurements(prev => ({ ...prev, hauteur3: e.target.value }))}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-orange-600 mb-1">HAUTEUR 4</label>
                        <input
                          type="number"
                          step="0.01"
                          value={pdMeasurements.hauteur4 || ''}
                          onChange={(e) => setPdMeasurements(prev => ({ ...prev, hauteur4: e.target.value }))}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Price and Quantity */}
          {(modalForm.product_id || (isOpticalItemType(modalType) && lensSelectionType === 'both_different' && (leftLensProduct.product_id || rightLensProduct.product_id))) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Prix (MAD)</label>
                {isOpticalItemType(modalType) && lensSelectionType === 'both_different' ? (
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-blue-600">{modalType === 'lentilles' ? 'Lentille Gauche' : 'Verre Gauche'}</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={priceInputValue(leftLensProduct.price)}
                        onChange={(e) => setLeftLensProduct(prev => ({ ...prev, price: e.target.value }))}
                        placeholder={`${leftLensProduct.product_id ? getProductSalePrice(leftLensProduct.product_id) : ''}`}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-green-600">{modalType === 'lentilles' ? 'Lentille Droit' : 'Verre Droit'}</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={priceInputValue(rightLensProduct.price)}
                        onChange={(e) => setRightLensProduct(prev => ({ ...prev, price: e.target.value }))}
                        placeholder={`${rightLensProduct.product_id ? getProductSalePrice(rightLensProduct.product_id) : ''}`}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                ) : (
              <input
                type="number"
                min="0"
                step="0.01"
                value={priceInputValue(modalForm.price)}
                onChange={(e) => handleModalChange('price', e.target.value)}
                placeholder={`${getProductSalePrice(modalForm.product_id)}`}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
                )}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Quantité</label>
              <input
                type="number"
                min="1"
                value={modalForm.quantity}
                onChange={(e) => handleModalChange('quantity', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          )}

          {/* Total */}
          {(modalForm.product_id || (isOpticalItemType(modalType) && (lensSelectionType === 'both_different' || lensSelectionType === 'both_same') && (leftLensProduct.product_id || rightLensProduct.product_id || modalForm.product_id))) && (
          <div className="text-lg font-semibold text-neutral-900">
              Total: {(() => {
                if (isOpticalItemType(modalType) && lensSelectionType === 'both_different') {
                  const leftPrice = itemPrice(leftLensProduct.price, leftLensProduct.product_id);
                  const rightPrice = itemPrice(rightLensProduct.price, rightLensProduct.product_id);
                  return (leftPrice + rightPrice) * (modalForm.quantity || 1);
                } else if (isOpticalItemType(modalType) && lensSelectionType === 'both_same') {
                  const singlePrice = itemPrice(modalForm.price, modalForm.product_id);
                  return singlePrice * 2 * (modalForm.quantity || 1);
                } else {
                  return itemPrice(modalForm.price, modalForm.product_id) * (modalForm.quantity || 1);
                }
              })()} MAD
          </div>
          )}

          {modalError && <div className="text-red-500 text-sm">{modalError}</div>}

          {!isOpticalItemType(modalType) && (
          <div className="text-xs text-neutral-500 bg-neutral-50 p-3 rounded-lg">
            💡 <strong>Note:</strong> Le produit sera ajouté à votre panier. Cliquez sur "Créer la commande" en bas de page pour finaliser la commande.
          </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-3 border-t border-neutral-200">
            <ModernButton variant="secondary" onClick={closeModal}>
              Annuler
            </ModernButton>
            <ModernButton 
              variant="primary" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!modalForm.product_id && !isOpticalItemType(modalType)) {
                  setModalError('Veuillez sélectionner un produit.');
                  return;
                }
                if (isOpticalItemType(modalType) && lensSelectionType === 'both_different') {
                  if (!leftLensProduct.product_id || !rightLensProduct.product_id) {
                    setModalError('Veuillez sélectionner les deux produits (gauche et droit).');
                    return;
                  }
                }
                if (!modalForm.quantity || modalForm.quantity < 1) {
                  setModalError('Veuillez entrer une quantité valide.');
                  return;
                }
                setModalError('');
                handleModalSave();
              }}
              type="button"
            >
              {modalEditIdx !== null ? 'Modifier' : 'Ajouter au panier'}
            </ModernButton>
          </div>
          </div>
        </div>
      </ModernModal>

      {/* Payment Reminder Modal */}
      <ModernModal 
        isOpen={showPaymentReminderModal} 
        onClose={() => setShowPaymentReminderModal(false)} 
        title="Paiement manquant" 
        size="md"
      >
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
              <DollarSign className="h-6 w-6 text-yellow-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun paiement saisi
            </h3>
            <p className="text-sm text-gray-500">
              Vous n'avez pas saisi de paiement pour cette commande. Souhaitez-vous ajouter un paiement maintenant ?
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <ModernButton
              variant="secondary"
              onClick={() => handlePaymentReminderResponse(false)}
              className="flex-1"
              disabled={loading}
            >
              {loading ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <ModernLoadingSpinner size="sm" />
                  Création...
                </span>
              ) : (
                'Créer sans paiement'
              )}
            </ModernButton>
            <ModernButton
              variant="primary"
              onClick={() => handlePaymentReminderResponse(true)}
              className="flex-1"
              disabled={loading}
            >
              Ajouter un paiement
            </ModernButton>
          </div>
        </div>
      </ModernModal>

      {/* Add Product Modal */}
      <ModernModal
        isOpen={showAddProductModal}
        onClose={() => setShowAddProductModal(false)}
        title="Ajouter un nouveau produit"
        size="lg"
      >
        <AddProductForm
          onSuccess={handleProductCreated}
          onCancel={() => setShowAddProductModal(false)}
        />
      </ModernModal>

      {/* Add Client Modal */}
      <ModernModal
        isOpen={showAddClientModal}
        onClose={() => setShowAddClientModal(false)}
        title="Ajouter un nouveau client"
        size="lg"
      >
        <AddClientForm
          onSuccess={handleClientCreated}
          onCancel={() => setShowAddClientModal(false)}
        />
      </ModernModal>

    </div>
  );
};

export default VentesNouveau; 